import * as React from 'react';
import { useState } from 'react';
import { styled } from '../../../theme';
import { DeleteAppPayload } from './DeleteAppPage';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { FormLayoutSection, FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { ParagraphWidget } from '../../../widgets/forms/ParagraphWidget';
import { LoaderSpinner } from '../../../widgets/LoaderWidget';
import { AppModel } from '@journeyapps-platform/lib-journey-core';
import { TableWidget } from '../../../widgets/table/TableWidget';
import { observer } from 'mobx-react';
import { useSystem } from '../../../hooks/useSystem';
import { InputWrapperWidget } from '../../../widgets/inputs';
import { ButtonType, ButtonWidget } from '../../../widgets/buttons/ButtonWidget';

export interface DeleteAppConfirmWidgetProps {
  payload: Partial<DeleteAppPayload>;
  app: AppModel;
  next: () => any;
  onCancel: () => any;
}

export const DeleteAppWarnWidget: React.FC<DeleteAppConfirmWidgetProps> = observer((props) => {
  const system = useSystem();
  const [error, setError] = useState<string>();
  const appDeployments = props.app.deployments.items.filter((dep) => !dep.model.managed);
  const hasDeployments = appDeployments.length > 0;

  const deleteDeployment = async (id: string) => {
    const dep = await system.deployConfigStore.resolve(id);
    try {
      await dep.delete();
    } catch (err) {
      console.error(`Failed to delete deployment (DeploymentID: ${id})`, err);
      setError(`Failed to delete deployment ${dep.model.label ? `${dep.model.label}` : ''}`);
    }
  };

  const getDeployments = () => {
    if (!hasDeployments) {
      return null;
    }
    return (
      <InputWrapperWidget error={error}>
        <S.Paragraph>{`The following deployments must be deleted in order to delete the app.`}</S.Paragraph>
        <S.Deployments>
          <TableWidget
            columns={[
              {
                key: 'name',
                display: 'Deployment'
              },
              {
                key: 'action',
                display: '',
                shrink: true,
                accessor: (event) => {
                  return (
                    <ButtonWidget
                      type={ButtonType.SECONDARY}
                      label="Delete"
                      action={async (event2, loading) => {
                        const confirm = await system.dialogStore.confirmDialog({
                          title: 'Delete deployment?',
                          message: 'This operation cannot be undone.'
                        });
                        if (!confirm) {
                          return;
                        }

                        loading(true);
                        await deleteDeployment(event.row.key);
                        loading(false);
                      }}
                    />
                  );
                }
              }
            ]}
            rows={
              appDeployments.map((dep) => {
                return { key: dep.id, cells: { name: dep.model.label } };
              }) || []
            }
          />
        </S.Deployments>
      </InputWrapperWidget>
    );
  };

  const getContent = (): FormLayoutSection => {
    return {
      key: 'warn_delete',
      elements: [
        <S.Container>
          {props.payload.loading ? (
            <S.Loader>
              <LoaderSpinner />
            </S.Loader>
          ) : (
            <>
              <S.Paragraph>
                This can <b>not be undone</b> and will permanently remove:
              </S.Paragraph>
              <S.Paragraph>
                • All of <b>{props.app.model.name}</b> data and metadata <br />• All deployments
              </S.Paragraph>
              {getDeployments()}
            </>
          )}
        </S.Container>
      ]
    };
  };

  const getButtons = () => {
    return {
      key: 'submit',
      elements: [
        <GroupedButtons
          btns={[
            {
              label: 'Cancel',
              action: () => {
                props.onCancel();
              }
            },
            {
              label: 'I Agree',
              disabled: props.payload.loading || hasDeployments,
              action: () => {
                props.next();
              }
            }
          ]}
        />
      ]
    };
  };

  return (
    <CenterFormWidget noLogo={true} primaryLabel={'Delete app'} orientation={CenterFormOrientation.ROW}>
      <FormLayoutWidget sections={[getContent(), getButtons()]} />
    </CenterFormWidget>
  );
});

const BASE_LABEL = 'delete-app-confirm';
namespace S {
  export const Container = styled.div`
    label: ${BASE_LABEL};
    min-width: 400px;
  `;

  export const Loader = styled.div`
    label: ${BASE_LABEL}__loader;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  export const Paragraph = styled(ParagraphWidget)`
    label: ${BASE_LABEL}__paragraph;
    text-align: left;
    margin-bottom: 16px;
  `;

  export const Deployments = styled.div`
    label: ${BASE_LABEL}__deployments;
    display: flex;
    flex-direction: column;
    background-color: ${(p) => p.theme.background};
    margin-bottom: 16px;
  `;
}
